import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiService } from '../../lib/api';
import { useNotifications } from '../../hooks/useNotifications';
import { useAdmin } from '../../hooks/useAdmin';

interface RecurringNotification {
  id: number;
  name: string;
  title: string;
  body: string;
  recipientType: 'all' | 'inactive' | 'specific';
  inactiveDays?: number;
  specificRecipients?: string[];
  scheduleType: 'daily' | 'weekly' | 'monthly';
  dayOfWeek?: number;
  dayOfMonth?: number;
  timeOfDay: string;
  timezone: string;
  enabled: boolean;
  lastScheduledAt?: string;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

const DAYS_OF_WEEK = [
  { value: 0, label: 'Sunday' },
  { value: 1, label: 'Monday' },
  { value: 2, label: 'Tuesday' },
  { value: 3, label: 'Wednesday' },
  { value: 4, label: 'Thursday' },
  { value: 5, label: 'Friday' },
  { value: 6, label: 'Saturday' },
];

const TIMEZONES = [
  { value: 'America/New_York', label: 'Eastern Time (ET)' },
  { value: 'America/Chicago', label: 'Central Time (CT)' },
  { value: 'America/Denver', label: 'Mountain Time (MT)' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
  { value: 'UTC', label: 'UTC' },
];

export default function RecurringNotifications() {
  const { requireAdmin } = useAdmin();
  const { showSuccess, showError } = useNotifications();
  const queryClient = useQueryClient();

  requireAdmin();

  const [isCreating, setIsCreating] = useState(false);
  const [editingNotification, setEditingNotification] = useState<RecurringNotification | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    title: '',
    body: '',
    recipientType: 'inactive' as 'all' | 'inactive' | 'specific',
    inactiveDays: 7,
    scheduleType: 'weekly' as 'daily' | 'weekly' | 'monthly',
    dayOfWeek: 0,
    dayOfMonth: 1,
    timeOfDay: '18:00',
    timezone: 'America/New_York',
  });

  // Fetch recurring notifications
  const { data: notifications, isLoading } = useQuery({
    queryKey: ['recurring-notifications'],
    queryFn: async () => {
      const response = await apiService.request<{ recurring: RecurringNotification[] }>('/api/v1/admin/recurring_notifications');
      return response.recurring;
    },
  });

  // Create mutation
  const createMutation = useMutation({
    mutationFn: async (data: typeof formData) => {
      return apiService.request('/api/v1/admin/recurring_notifications', {
        method: 'POST',
        body: JSON.stringify(data),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['recurring-notifications'] });
      showSuccess('Recurring notification created successfully');
      resetForm();
      setIsCreating(false);
    },
    onError: (error: any) => {
      showError(error.message || 'Failed to create recurring notification');
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: number; data: typeof formData }) => {
      return apiService.request(`/api/v1/admin/recurring_notifications/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['recurring-notifications'] });
      showSuccess('Recurring notification updated successfully');
      resetForm();
      setEditingNotification(null);
    },
    onError: (error: any) => {
      showError(error.message || 'Failed to update recurring notification');
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async (id: number) => {
      return apiService.request(`/api/v1/admin/recurring_notifications/${id}`, {
        method: 'DELETE',
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['recurring-notifications'] });
      showSuccess('Recurring notification deleted successfully');
    },
    onError: (error: any) => {
      showError(error.message || 'Failed to delete recurring notification');
    },
  });

  // Toggle mutation
  const toggleMutation = useMutation({
    mutationFn: async (id: number) => {
      return apiService.request(`/api/v1/admin/recurring_notifications/${id}/toggle`, {
        method: 'POST',
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['recurring-notifications'] });
      showSuccess('Recurring notification toggled successfully');
    },
    onError: (error: any) => {
      showError(error.message || 'Failed to toggle recurring notification');
    },
  });

  const resetForm = () => {
    setFormData({
      name: '',
      title: '',
      body: '',
      recipientType: 'inactive',
      inactiveDays: 7,
      scheduleType: 'weekly',
      dayOfWeek: 0,
      dayOfMonth: 1,
      timeOfDay: '18:00',
      timezone: 'America/New_York',
    });
  };

  const handleEdit = (notification: RecurringNotification) => {
    setEditingNotification(notification);
    setFormData({
      name: notification.name,
      title: notification.title,
      body: notification.body,
      recipientType: notification.recipientType,
      inactiveDays: notification.inactiveDays || 7,
      scheduleType: notification.scheduleType,
      dayOfWeek: notification.dayOfWeek || 0,
      dayOfMonth: notification.dayOfMonth || 1,
      timeOfDay: notification.timeOfDay,
      timezone: notification.timezone,
    });
    setIsCreating(true);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (editingNotification) {
      updateMutation.mutate({ id: editingNotification.id, data: formData });
    } else {
      createMutation.mutate(formData);
    }
  };

  const handleCancel = () => {
    resetForm();
    setIsCreating(false);
    setEditingNotification(null);
  };

  if (isLoading) {
    return <div className="p-6">Loading...</div>;
  }

  return (
    <div className="max-w-7xl mx-auto px-6 py-8">
      <div className="mb-8">
        <h2 className="text-2xl font-bold theme-text mb-2">Recurring Push Notifications</h2>
        <p className="theme-text-secondary">
          Schedule automatic push notifications to be sent on a recurring basis to engage inactive users.
        </p>
      </div>

      {/* Create/Edit Form */}
      {isCreating && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 mb-6">
          <h3 className="text-lg font-semibold theme-text mb-4">
            {editingNotification ? 'Edit' : 'Create'} Recurring Notification
          </h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium theme-text mb-1">
                Name
              </label>
              <input
                type="text"
                required
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md theme-surface theme-text"
                placeholder="e.g., Weekly Sunday Reminder"
              />
            </div>

            <div>
              <label className="block text-sm font-medium theme-text mb-1">
                Notification Title
              </label>
              <input
                type="text"
                required
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md theme-surface theme-text"
                placeholder="e.g., New Activity on Chime"
              />
            </div>

            <div>
              <label className="block text-sm font-medium theme-text mb-1">
                Notification Body
              </label>
              <textarea
                required
                value={formData.body}
                onChange={(e) => setFormData({ ...formData, body: e.target.value })}
                rows={4}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md theme-surface theme-text"
                placeholder="Message content..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium theme-text mb-1">
                Recipient Type
              </label>
              <select
                value={formData.recipientType}
                onChange={(e) => setFormData({ ...formData, recipientType: e.target.value as any })}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md theme-surface theme-text"
              >
                <option value="all">All Users</option>
                <option value="inactive">Inactive Users</option>
              </select>
            </div>

            {formData.recipientType === 'inactive' && (
              <div>
                <label className="block text-sm font-medium theme-text mb-1">
                  Inactive Days
                </label>
                <input
                  type="number"
                  min="1"
                  value={formData.inactiveDays}
                  onChange={(e) => setFormData({ ...formData, inactiveDays: parseInt(e.target.value) })}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md theme-surface theme-text"
                />
                <p className="text-xs theme-text-secondary mt-1">
                  Send to users who haven't logged in for this many days
                </p>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium theme-text mb-1">
                  Schedule Type
                </label>
                <select
                  value={formData.scheduleType}
                  onChange={(e) => setFormData({ ...formData, scheduleType: e.target.value as any })}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md theme-surface theme-text"
                >
                  <option value="daily">Daily</option>
                  <option value="weekly">Weekly</option>
                  <option value="monthly">Monthly</option>
                </select>
              </div>

              {formData.scheduleType === 'weekly' && (
                <div>
                  <label className="block text-sm font-medium theme-text mb-1">
                    Day of Week
                  </label>
                  <select
                    value={formData.dayOfWeek}
                    onChange={(e) => setFormData({ ...formData, dayOfWeek: parseInt(e.target.value) })}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md theme-surface theme-text"
                  >
                    {DAYS_OF_WEEK.map((day) => (
                      <option key={day.value} value={day.value}>
                        {day.label}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {formData.scheduleType === 'monthly' && (
                <div>
                  <label className="block text-sm font-medium theme-text mb-1">
                    Day of Month
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="31"
                    value={formData.dayOfMonth}
                    onChange={(e) => setFormData({ ...formData, dayOfMonth: parseInt(e.target.value) })}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md theme-surface theme-text"
                  />
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium theme-text mb-1">
                  Time of Day
                </label>
                <input
                  type="time"
                  value={formData.timeOfDay}
                  onChange={(e) => setFormData({ ...formData, timeOfDay: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md theme-surface theme-text"
                />
              </div>

              <div>
                <label className="block text-sm font-medium theme-text mb-1">
                  Timezone
                </label>
                <select
                  value={formData.timezone}
                  onChange={(e) => setFormData({ ...formData, timezone: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md theme-surface theme-text"
                >
                  {TIMEZONES.map((tz) => (
                    <option key={tz.value} value={tz.value}>
                      {tz.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <button
                type="button"
                onClick={handleCancel}
                className="px-4 py-2 rounded-md bg-gray-200 dark:bg-gray-700 theme-text hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={createMutation.isPending || updateMutation.isPending}
                className="px-4 py-2 rounded-md bg-blue-600 text-white hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                {createMutation.isPending || updateMutation.isPending ? 'Saving...' : editingNotification ? 'Update' : 'Create'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Create Button */}
      {!isCreating && (
        <button
          onClick={() => setIsCreating(true)}
          className="mb-6 px-4 py-2 rounded-md bg-blue-600 text-white hover:bg-blue-700 transition-colors"
        >
          + Create Recurring Notification
        </button>
      )}

      {/* Notifications List */}
      <div className="space-y-4">
        {notifications && notifications.length === 0 && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 text-center">
            <p className="theme-text-secondary">No recurring notifications configured yet.</p>
          </div>
        )}

        {notifications?.map((notification) => (
          <div
            key={notification.id}
            className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6"
          >
            <div className="flex items-start justify-between mb-4">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <h3 className="text-lg font-semibold theme-text">{notification.name}</h3>
                  <span
                    className={`px-2 py-1 text-xs rounded-full ${
                      notification.enabled
                        ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                        : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                    }`}
                  >
                    {notification.enabled ? 'Enabled' : 'Disabled'}
                  </span>
                </div>
                <p className="text-sm font-medium theme-text mb-1">{notification.title}</p>
                <p className="text-sm theme-text-secondary mb-3">{notification.body}</p>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm theme-text-secondary">
                  <div>
                    <span className="font-medium">Schedule:</span>{' '}
                    {notification.scheduleType === 'weekly' && DAYS_OF_WEEK.find((d) => d.value === notification.dayOfWeek)?.label}
                    {notification.scheduleType === 'daily' && 'Daily'}
                    {notification.scheduleType === 'monthly' && `Day ${notification.dayOfMonth}`}
                  </div>
                  <div>
                    <span className="font-medium">Time:</span> {notification.timeOfDay}
                  </div>
                  <div>
                    <span className="font-medium">Timezone:</span>{' '}
                    {TIMEZONES.find((tz) => tz.value === notification.timezone)?.label}
                  </div>
                  <div>
                    <span className="font-medium">Recipients:</span>{' '}
                    {notification.recipientType === 'inactive'
                      ? `Inactive (${notification.inactiveDays}+ days)`
                      : 'All Users'}
                  </div>
                </div>

                {notification.lastScheduledAt && (
                  <div className="mt-2 text-xs theme-text-secondary">
                    Last scheduled: {new Date(notification.lastScheduledAt).toLocaleString()}
                  </div>
                )}
              </div>

              <div className="flex gap-2 ml-4">
                <button
                  onClick={() => toggleMutation.mutate(notification.id)}
                  disabled={toggleMutation.isPending}
                  className="px-3 py-1 rounded-md bg-gray-200 dark:bg-gray-700 theme-text hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors text-sm"
                  title={notification.enabled ? 'Disable' : 'Enable'}
                >
                  {notification.enabled ? 'Disable' : 'Enable'}
                </button>
                <button
                  onClick={() => handleEdit(notification)}
                  className="px-3 py-1 rounded-md bg-blue-600 text-white hover:bg-blue-700 transition-colors text-sm"
                >
                  Edit
                </button>
                <button
                  onClick={() => {
                    if (confirm('Are you sure you want to delete this recurring notification?')) {
                      deleteMutation.mutate(notification.id);
                    }
                  }}
                  disabled={deleteMutation.isPending}
                  className="px-3 py-1 rounded-md bg-red-600 text-white hover:bg-red-700 transition-colors text-sm"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
